import React, { useState, useEffect, useMemo } from "react";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import { useNavigate } from "react-router-dom";
import { getSessions } from "../../../api/SessionsService";
import { StaffList } from "../../../api/Staff";
import { SweetAlertShow, LoadingDiv } from "../../master/Master";
import { StaffReportList } from "../../../api/ReportsServices";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { CSVLink } from "react-csv";
import { setPrintContent } from "../../../PrintStore";
import ReportLayout from "./ReportLayout";

const StaffReport = () => {
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSessions] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportData, setReportData] = useState([]);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("table");

  const navigate = useNavigate();

  /* -------------------------------------------------------
     Fetch Report
  ------------------------------------------------------- */

  const getList = async () => {
    setLoading(true);

    const addUrl = `&staff_id=${selectedStaff}&start_date=${startDate}&end_date=${endDate}`;

    try {
      const response = await StaffReportList(addUrl);

      if (response?.data?.status) {
        setReportData(response?.data);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "No report data found.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.message ??
          "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getList();
  }, [selectedStaff, startDate, endDate]);

  /* -------------------------------------------------------
     Fetch Staff
  ------------------------------------------------------- */

  const getStaffs = async () => {
    try {
      const addUrl = `&session_id=${selectedSession}&search=${search}`;

      const response = await StaffList(addUrl);

      if (response?.data?.status) {
        setStaffList(response?.data);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to fetch staff.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Something went wrong.",
      );
    }
  };

  useEffect(() => {
    getStaffs();
  }, [selectedSession, search]);

  /* -------------------------------------------------------
     Fetch Sessions
  ------------------------------------------------------- */

  const getSession = async () => {
    setLoading(true);

    try {
      const response = await getSessions();

      if (response?.data?.status) {
        const sessionData = response?.data?.data || [];

        const activeSession = sessionData.find(
          (session) => session.status === "1",
        );

        setSessions(sessionData);

        if (activeSession) {
          setSelectedSessions(activeSession.id);
        } else if (sessionData.length > 0) {
          setSelectedSessions(sessionData[0].id);
        }
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.message ??
          "Unable to load sessions.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSession();
  }, []);

  /* -------------------------------------------------------
     Report Rows
  ------------------------------------------------------- */

  const rows = Array.isArray(reportData?.data) ? reportData.data : [];

  /* -------------------------------------------------------
     KPI Calculations
  ------------------------------------------------------- */

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.admissions += Number(row?.total_students_admitted || 0);
        acc.enquiries += Number(row?.total_enquiry || 0);
        acc.enquiryAdmissions += Number(row?.total_enquiry_admission || 0);
        acc.followupEnquiries += Number(row?.total_followup_enquiry || 0);
        acc.followupFees += Number(row?.total_followup_fees || 0);
        acc.followupMarketing += Number(row?.total_followup_marketing || 0);

        return acc;
      },
      {
        admissions: 0,
        enquiries: 0,
        enquiryAdmissions: 0,
        followupEnquiries: 0,
        followupFees: 0,
        followupMarketing: 0,
      },
    );
  }, [rows]);

  /* -------------------------------------------------------
     Chart Data
  ------------------------------------------------------- */

  const chartData = rows.map((staff) => ({
    name: staff?.name || "Unknown",
    "Student Admission": Number(staff?.total_students_admitted || 0),
    "Total Enquiries": Number(staff?.total_enquiry || 0),
    "Enquiry Admissions": Number(staff?.total_enquiry_admission || 0),
    "Follow-up Enquiries": Number(staff?.total_followup_enquiry || 0),
    "Follow-up Fees": Number(staff?.total_followup_fees || 0),
    "Follow-up Marketing": Number(staff?.total_followup_marketing || 0),
  }));

  /* -------------------------------------------------------
   ANALYTICS DATA
------------------------------------------------------- */

  const activeStaffData = useMemo(() => {
    return rows
      .map((staff) => {
        const admissions = Number(staff?.total_students_admitted || 0);
        const enquiries = Number(staff?.total_enquiry || 0);
        const enquiryAdmissions = Number(staff?.total_enquiry_admission || 0);
        const followups = Number(staff?.total_followup_enquiry || 0);
        const fees = Number(staff?.total_followup_fees || 0);
        const marketing = Number(staff?.total_followup_marketing || 0);

        const totalActivity =
          admissions +
          enquiries +
          enquiryAdmissions +
          followups +
          fees +
          marketing;

        return {
          name: staff?.name || "Unknown",
          staff_type: staff?.staff_type || "Other",
          admissions,
          enquiries,
          enquiryAdmissions,
          followups,
          fees,
          marketing,
          totalActivity,
        };
      })
      .filter((staff) => staff.totalActivity > 0)
      .sort((a, b) => b.totalActivity - a.totalActivity);
  }, [rows]);

  /* -------------------------------------------------------
   TOP 10 STAFF
------------------------------------------------------- */

  const topStaffData = useMemo(() => {
    return activeStaffData.slice(0, 10);
  }, [activeStaffData]);

  /* -------------------------------------------------------
   ACTIVITY DISTRIBUTION
------------------------------------------------------- */

  const activityDistribution = useMemo(() => {
    return [
      {
        name: "Admissions",
        value: totals.admissions,
      },
      {
        name: "Enquiries",
        value: totals.enquiries,
      },
      {
        name: "Enquiry Admissions",
        value: totals.enquiryAdmissions,
      },
      {
        name: "Follow-ups",
        value: totals.followupEnquiries,
      },
      {
        name: "Follow-up Fees",
        value: totals.followupFees,
      },
      {
        name: "Marketing",
        value: totals.followupMarketing,
      },
    ].filter((item) => item.value > 0);
  }, [totals]);

  /* -------------------------------------------------------
   STAFF TYPE SUMMARY
------------------------------------------------------- */

  const staffTypeSummary = useMemo(() => {
    const grouped = {};

    rows.forEach((staff) => {
      const type = staff?.staff_type || "Other";

      if (!grouped[type]) {
        grouped[type] = {
          type,
          staff: 0,
          admissions: 0,
          enquiries: 0,
          followups: 0,
        };
      }

      grouped[type].staff += 1;

      grouped[type].admissions += Number(staff?.total_students_admitted || 0);

      grouped[type].enquiries += Number(staff?.total_enquiry || 0);

      grouped[type].followups += Number(staff?.total_followup_enquiry || 0);
    });

    return Object.values(grouped);
  }, [rows]);

  /* -------------------------------------------------------
   ACTIVE STAFF %
------------------------------------------------------- */

  const activeStaffPercentage = rows.length
    ? Math.round((activeStaffData.length / rows.length) * 100)
    : 0;

  /* -------------------------------------------------------
     Reset
  ------------------------------------------------------- */

  const handleReset = () => {
    setSelectedStaff("");
    setStartDate("");
    setEndDate("");
    setSearch("");
  };

  /* -------------------------------------------------------
     Print
  ------------------------------------------------------- */

  const handlePrint = () => {
    const printTable = (
      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <caption className="text-center fw-bold fs-5 mb-3">
            Staff Leads Report
          </caption>

          <thead className="table-primary text-nowrap">
            <tr>
              <th>#</th>
              <th>Staff Name</th>
              <th>Type</th>
              <th>Phone</th>
              <th>Student Admission</th>
              <th>Total Enquiries</th>
              <th>Enquiry Admissions</th>
              <th>Follow-up Enquiries</th>
              <th>Follow-up Fees</th>
              <th>Follow-up Marketing</th>
            </tr>
          </thead>

          <tbody>
            {rows.length > 0 ? (
              rows.map((row, index) => (
                <tr key={row.staff_id || index}>
                  <td>{index + 1}</td>
                  <td>{row.name}</td>
                  <td>{row.staff_type}</td>
                  <td>{row.mobile}</td>
                  <td>{row.total_students_admitted || 0}</td>
                  <td>{row.total_enquiry || 0}</td>
                  <td>{row.total_enquiry_admission || 0}</td>
                  <td>{row.total_followup_enquiry || 0}</td>
                  <td>{row.total_followup_fees || 0}</td>
                  <td>{row.total_followup_marketing || 0}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="text-center">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );

    setPrintContent(printTable);
    navigate("/print");
  };

  /* -------------------------------------------------------
     Selected Session
  ------------------------------------------------------- */

  const selectedSessionData = sessions.find(
    (session) => String(session.id) === String(selectedSession),
  );

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="container-fluid py-3">
              <div className="row">
                <ReportLayout />

                <div className="col-12">
                  {/* ==================================================
                      PAGE HEADER
                  ================================================== */}

                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <div
                              className="bg-primary text-white rounded-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: 42,
                                height: 42,
                              }}
                            >
                              <i className="ri-team-line fs-4"></i>
                            </div>

                            <div>
                              <h4 className="mb-0 fw-semibold">Staff Report</h4>

                              <small className="text-muted">
                                Track staff performance, enquiries and
                                admissions
                              </small>
                            </div>
                          </div>
                        </div>

                        <div
                          className="form-floating form-floating-outline"
                          style={{ minWidth: 220 }}
                        >
                          <select
                            className="form-select"
                            value={selectedSession}
                            onChange={(e) =>
                              setSelectedSessions(e.target.value)
                            }
                          >
                            {sessions.map((session) => (
                              <option key={session.id} value={session.id}>
                                {session.session}
                                {session.status === "1"
                                  ? " • Active"
                                  : " • Inactive"}
                              </option>
                            ))}
                          </select>

                          <label>
                            <i className="ri-calendar-line me-1"></i>
                            Session
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ==================================================
                      FILTERS
                  ================================================== */}

                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-header bg-transparent border-0 pb-0">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1 fw-semibold">
                            <i className="ri-filter-3-line me-2 text-primary"></i>
                            Report Filters
                          </h6>

                          <small className="text-muted">
                            Refine the report using staff and date range
                          </small>
                        </div>

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleReset}
                        >
                          <i className="ri-refresh-line me-1"></i>
                          Reset
                        </button>
                      </div>
                    </div>

                    <div className="card-body">
                      <div className="row g-3">
                        {/* Staff */}

                        <div className="col-xl-4 col-lg-4 col-md-6">
                          <div className="form-floating form-floating-outline">
                            <select
                              className="form-select"
                              value={selectedStaff}
                              onChange={(e) => setSelectedStaff(e.target.value)}
                            >
                              <option value="">All Staff</option>

                              {staffList?.data?.map((staff) => (
                                <option key={staff.id} value={staff.id}>
                                  {staff.name}
                                </option>
                              ))}
                            </select>

                            <label>
                              <i className="ri-user-line me-1"></i>
                              Staff Member
                            </label>
                          </div>
                        </div>

                        {/* Start Date */}

                        <div className="col-xl-3 col-lg-3 col-md-6">
                          <div className="form-floating form-floating-outline">
                            <input
                              type="date"
                              className="form-control"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                            />

                            <label>
                              <i className="ri-calendar-event-line me-1"></i>
                              Start Date
                            </label>
                          </div>
                        </div>

                        {/* End Date */}

                        <div className="col-xl-3 col-lg-3 col-md-6">
                          <div className="form-floating form-floating-outline">
                            <input
                              type="date"
                              className="form-control"
                              value={endDate}
                              min={startDate || undefined}
                              onChange={(e) => setEndDate(e.target.value)}
                            />

                            <label>
                              <i className="ri-calendar-check-line me-1"></i>
                              End Date
                            </label>
                          </div>
                        </div>

                        {/* Search Staff */}

                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="form-floating form-floating-outline">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                            />

                            <label>
                              <i className="ri-search-line me-1"></i>
                              Search Staff
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Active filter summary */}

                      <div className="d-flex flex-wrap gap-2 mt-3">
                        <span className="badge bg-label-primary">
                          <i className="ri-calendar-line me-1"></i>
                          {selectedSessionData?.session || "All Sessions"}
                        </span>

                        {selectedStaff && (
                          <span className="badge bg-label-info">
                            <i className="ri-user-line me-1"></i>
                            {
                              staffList?.data?.find(
                                (staff) =>
                                  String(staff.id) === String(selectedStaff),
                              )?.name
                            }
                          </span>
                        )}

                        {(startDate || endDate) && (
                          <span className="badge bg-label-warning">
                            <i className="ri-time-line me-1"></i>
                            {startDate || "Start"} → {endDate || "Today"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ==================================================
                      KPI CARDS
                  ================================================== */}

                  <div className="row g-3 mb-3">
                    <div className="col-xl-2 col-lg-4 col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between">
                            <div>
                              <small className="text-muted">Staff</small>

                              <h4 className="mb-0 mt-1 fw-bold">
                                {rows.length}
                              </h4>
                            </div>

                            <div className="avatar">
                              <span className="avatar-initial rounded bg-label-primary">
                                <i className="ri-team-line"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-2 col-lg-4 col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between">
                            <div>
                              <small className="text-muted">Admissions</small>

                              <h4 className="mb-0 mt-1 fw-bold">
                                {totals.admissions}
                              </h4>
                            </div>

                            <div className="avatar">
                              <span className="avatar-initial rounded bg-label-success">
                                <i className="ri-user-add-line"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-2 col-lg-4 col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between">
                            <div>
                              <small className="text-muted">Enquiries</small>

                              <h4 className="mb-0 mt-1 fw-bold">
                                {totals.enquiries}
                              </h4>
                            </div>

                            <div className="avatar">
                              <span className="avatar-initial rounded bg-label-info">
                                <i className="ri-question-line"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-2 col-lg-4 col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between">
                            <div>
                              <small className="text-muted">
                                Enquiry Admissions
                              </small>

                              <h4 className="mb-0 mt-1 fw-bold">
                                {totals.enquiryAdmissions}
                              </h4>
                            </div>

                            <div className="avatar">
                              <span className="avatar-initial rounded bg-label-warning">
                                <i className="ri-checkbox-circle-line"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-2 col-lg-4 col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between">
                            <div>
                              <small className="text-muted">Follow-ups</small>

                              <h4 className="mb-0 mt-1 fw-bold">
                                {totals.followupEnquiries}
                              </h4>
                            </div>

                            <div className="avatar">
                              <span className="avatar-initial rounded bg-label-danger">
                                <i className="ri-loop-left-line"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-2 col-lg-4 col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between">
                            <div>
                              <small className="text-muted">
                                Follow-up Fees
                              </small>

                              <h4 className="mb-0 mt-1 fw-bold">
                                {totals.followupFees}
                              </h4>
                            </div>

                            <div className="avatar">
                              <span className="avatar-initial rounded bg-label-success">
                                <i className="ri-money-rupee-circle-line"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ==================================================
                      REPORT HEADER / TABS
                  ================================================== */}

                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-transparent border-bottom">
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <div>
                          <h6 className="mb-1 fw-semibold">
                            Performance Overview
                          </h6>

                          <small className="text-muted">
                            {rows.length} staff record
                            {rows.length !== 1 ? "s" : ""} found
                          </small>
                        </div>

                        <div className="d-flex gap-2">
                          <CSVLink
                            data={rows}
                            filename="staff_report.csv"
                            className="btn btn-sm btn-outline-success"
                          >
                            <i className="ri-file-excel-2-line me-1"></i>
                            Export
                          </CSVLink>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={handlePrint}
                          >
                            <i className="ri-printer-line me-1"></i>
                            Print
                          </button>
                        </div>
                      </div>

                      {/* Tabs */}

                      <div className="nav nav-pills nav-fill mt-3 report-tabs">
                        <button
                          type="button"
                          className={`nav-link ${
                            activeTab === "table" ? "active" : ""
                          }`}
                          onClick={() => setActiveTab("table")}
                        >
                          <i className="ri-table-2-line me-1"></i>
                          Data Table
                        </button>

                        <button
                          type="button"
                          className={`nav-link ${
                            activeTab === "chart" ? "active" : ""
                          }`}
                          onClick={() => setActiveTab("chart")}
                        >
                          <i className="ri-bar-chart-2-line me-1"></i>
                          Analytics
                        </button>
                      </div>
                    </div>

                    {/* ==================================================
                        TABLE
                    ================================================== */}

                    {activeTab === "table" && (
                      <div className="card-body p-0">
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead className="text-nowrap">
                              <tr>
                                <th className="ps-4">#</th>

                                <th>Staff</th>

                                <th>Type</th>
                                <th>Phone</th>

                                <th className="text-center">Admissions</th>

                                <th className="text-center">Enquiries</th>

                                <th className="text-center">
                                  Enquiry Admissions
                                </th>

                                <th className="text-center">Follow-ups</th>

                                <th className="text-center">Fees</th>

                                <th className="text-center pe-4">Marketing</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.length > 0 ? (
                                rows.map((row, index) => (
                                  <tr key={row.staff_id || index}>
                                    <td className="ps-4 text-muted">
                                      {index + 1}
                                    </td>

                                    <td>
                                      <div className="d-flex align-items-center gap-2">
                                        <div
                                          className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center"
                                          style={{
                                            width: 36,
                                            height: 36,
                                          }}
                                        >
                                          <i className="ri-user-line"></i>
                                        </div>

                                        <div>
                                          <div className="fw-semibold text-capitalize">
                                            {row.name || "Unknown"}
                                          </div>
                                        </div>
                                      </div>
                                    </td>

                                    <td>
                                      <span className="badge bg-label-secondary">
                                        {row.staff_type || "-"}
                                      </span>
                                    </td>

                                    <td>{row.mobile || "-"}</td>

                                    <td className="text-center">
                                      <span className="badge bg-label-success fs-6">
                                        {row.total_students_admitted || 0}
                                      </span>
                                    </td>

                                    <td className="text-center">
                                      {row.total_enquiry || 0}
                                    </td>

                                    <td className="text-center">
                                      {row.total_enquiry_admission || 0}
                                    </td>

                                    <td className="text-center">
                                      {row.total_followup_enquiry || 0}
                                    </td>

                                    <td className="text-center">
                                      {row.total_followup_fees || 0}
                                    </td>

                                    <td className="text-center pe-4">
                                      {row.total_followup_marketing || 0}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="10" className="text-center py-5">
                                    <div className="text-muted">
                                      <i className="ri-file-search-line fs-1 d-block mb-2"></i>

                                      <h6 className="mb-1">
                                        No report data found
                                      </h6>

                                      <small>
                                        Try changing the filters or date range.
                                      </small>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>

                            {rows.length > 0 && (
                              <tfoot>
                                <tr className="fw-semibold">
                                  <td colSpan="4" className="text-end">
                                    Total
                                  </td>

                                  <td className="text-center">
                                    {totals.admissions}
                                  </td>

                                  <td className="text-center">
                                    {totals.enquiries}
                                  </td>

                                  <td className="text-center">
                                    {totals.enquiryAdmissions}
                                  </td>

                                  <td className="text-center">
                                    {totals.followupEnquiries}
                                  </td>

                                  <td className="text-center">
                                    {totals.followupFees}
                                  </td>

                                  <td className="text-center">
                                    {totals.followupMarketing}
                                  </td>
                                </tr>
                              </tfoot>
                            )}
                          </table>
                        </div>
                      </div>
                    )}

                    {/* ==================================================
                        CHART
                    ================================================== */}

                    {activeTab === "chart" && (
                      <div className="card-body">
                        {/* =====================================================
        ANALYTICS HEADER
    ===================================================== */}

                        <div className="analytics-header mb-4">
                          <div>
                            <h5 className="mb-1 fw-semibold">
                              Staff Analytics
                            </h5>

                            <p className="text-muted mb-0">
                              Performance insights based on admissions,
                              enquiries and follow-up activities.
                            </p>
                          </div>

                          <div className="analytics-summary">
                            <div className="analytics-summary-item">
                              <span className="analytics-summary-label">
                                Active Staff
                              </span>

                              <strong>{activeStaffData.length}</strong>

                              <small>/ {rows.length}</small>
                            </div>

                            <div className="analytics-summary-item">
                              <span className="analytics-summary-label">
                                Activity Rate
                              </span>

                              <strong>{activeStaffPercentage}%</strong>
                            </div>
                          </div>
                        </div>

                        {/* =====================================================
        NO ACTIVITY
    ===================================================== */}

                        {activeStaffData.length === 0 ? (
                          <div className="analytics-empty">
                            <div className="analytics-empty-icon">
                              <i className="ri-bar-chart-grouped-line"></i>
                            </div>

                            <h6 className="mt-3">No staff activity found</h6>

                            <p className="text-muted mb-0">
                              There is currently no admission, enquiry or
                              follow-up activity for the selected filters.
                            </p>
                          </div>
                        ) : (
                          <>
                            {/* =================================================
            TOP STAFF + ACTIVITY DISTRIBUTION
        ================================================= */}

                            <div className="row g-3 mb-3">
                              {/* TOP STAFF */}

                              <div className="col-xl-8 col-lg-7">
                                <div className="analytics-card h-100">
                                  <div className="analytics-card-header">
                                    <div>
                                      <h6 className="mb-1">
                                        Top Staff Performance
                                      </h6>

                                      <small className="text-muted">
                                        Staff with measurable activity
                                      </small>
                                    </div>

                                    <span className="analytics-card-badge">
                                      Top {Math.min(10, activeStaffData.length)}
                                    </span>
                                  </div>

                                  <div
                                    style={{
                                      width: "100%",
                                      height: 380,
                                    }}
                                  >
                                    <ResponsiveContainer
                                      width="100%"
                                      height="100%"
                                    >
                                      <BarChart
                                        data={topStaffData}
                                        layout="vertical"
                                        margin={{
                                          top: 10,
                                          right: 25,
                                          left: 20,
                                          bottom: 10,
                                        }}
                                        barCategoryGap="28%"
                                      >
                                        <CartesianGrid
                                          strokeDasharray="3 3"
                                          horizontal={false}
                                          opacity={0.35}
                                        />

                                        <XAxis
                                          type="number"
                                          allowDecimals={false}
                                        />

                                        <YAxis
                                          type="category"
                                          dataKey="name"
                                          width={145}
                                          tick={{
                                            fontSize: 12,
                                          }}
                                        />

                                        <Tooltip
                                          cursor={{
                                            fill: "rgba(115,103,240,0.06)",
                                          }}
                                          formatter={(value) =>
                                            Number(value).toLocaleString()
                                          }
                                        />

                                        <Bar
                                          dataKey="totalActivity"
                                          name="Total Activity"
                                          fill="#7367F0"
                                          radius={[0, 6, 6, 0]}
                                          barSize={22}
                                        />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </div>
                                </div>
                              </div>

                              {/* ACTIVITY DISTRIBUTION */}

                              <div className="col-xl-4 col-lg-5">
                                <div className="analytics-card h-100">
                                  <div className="analytics-card-header">
                                    <div>
                                      <h6 className="mb-1">
                                        Activity Distribution
                                      </h6>

                                      <small className="text-muted">
                                        Overall activity breakdown
                                      </small>
                                    </div>
                                  </div>

                                  {activityDistribution.length > 0 ? (
                                    <>
                                      <div
                                        style={{
                                          height: 240,
                                          width: "100%",
                                        }}
                                      >
                                        <ResponsiveContainer
                                          width="100%"
                                          height="100%"
                                        >
                                          <PieChart>
                                            <Pie
                                              data={activityDistribution}
                                              dataKey="value"
                                              nameKey="name"
                                              cx="50%"
                                              cy="50%"
                                              innerRadius={60}
                                              outerRadius={90}
                                              paddingAngle={3}
                                            >
                                              {activityDistribution.map(
                                                (_, index) => (
                                                  <Cell
                                                    key={`cell-${index}`}
                                                    fill={
                                                      [
                                                        "#7367F0",
                                                        "#28C76F",
                                                        "#FF9F43",
                                                        "#EA5455",
                                                        "#00CFE8",
                                                        "#A8AAAE",
                                                      ][index % 6]
                                                    }
                                                  />
                                                ),
                                              )}
                                            </Pie>

                                            <Tooltip
                                              formatter={(value) =>
                                                Number(value).toLocaleString()
                                              }
                                            />
                                          </PieChart>
                                        </ResponsiveContainer>
                                      </div>

                                      <div className="activity-legend">
                                        {activityDistribution.map(
                                          (item, index) => (
                                            <div
                                              className="activity-legend-item"
                                              key={item.name}
                                            >
                                              <span
                                                className="activity-dot"
                                                style={{
                                                  backgroundColor: [
                                                    "#7367F0",
                                                    "#28C76F",
                                                    "#FF9F43",
                                                    "#EA5455",
                                                    "#00CFE8",
                                                    "#A8AAAE",
                                                  ][index % 6],
                                                }}
                                              ></span>

                                              <span className="activity-name">
                                                {item.name}
                                              </span>

                                              <strong className="ms-auto">
                                                {Number(
                                                  item.value,
                                                ).toLocaleString()}
                                              </strong>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-center text-muted py-5">
                                      No activity available
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* =================================================
            PERFORMANCE DETAILS
        ================================================= */}

                            <div className="row g-3">
                              {/* STAFF TYPE */}

                              <div className="col-xl-6">
                                <div className="analytics-card">
                                  <div className="analytics-card-header">
                                    <div>
                                      <h6 className="mb-1">
                                        Staff Type Overview
                                      </h6>

                                      <small className="text-muted">
                                        Activity grouped by staff category
                                      </small>
                                    </div>
                                  </div>

                                  <div className="table-responsive">
                                    <table className="table table-sm align-middle mb-0">
                                      <thead>
                                        <tr>
                                          <th>Staff Type</th>

                                          <th className="text-center">Staff</th>

                                          <th className="text-center">
                                            Admissions
                                          </th>

                                          <th className="text-center">
                                            Enquiries
                                          </th>

                                          <th className="text-center">
                                            Follow-ups
                                          </th>
                                        </tr>
                                      </thead>

                                      <tbody>
                                        {staffTypeSummary.map((item) => (
                                          <tr key={item.type}>
                                            <td>
                                              <span className="fw-medium">
                                                {item.type}
                                              </span>
                                            </td>

                                            <td className="text-center">
                                              {item.staff}
                                            </td>

                                            <td className="text-center">
                                              <span className="badge bg-label-success">
                                                {item.admissions}
                                              </span>
                                            </td>

                                            <td className="text-center">
                                              {item.enquiries}
                                            </td>

                                            <td className="text-center">
                                              {item.followups}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>

                              {/* TOP PERFORMERS */}

                              <div className="col-xl-6">
                                <div className="analytics-card">
                                  <div className="analytics-card-header">
                                    <div>
                                      <h6 className="mb-1">
                                        Staff Activity Ranking
                                      </h6>

                                      <small className="text-muted">
                                        Based on total recorded activity
                                      </small>
                                    </div>
                                  </div>

                                  <div className="staff-ranking">
                                    {activeStaffData
                                      .slice(0, 5)
                                      .map((staff, index) => (
                                        <div
                                          className="staff-ranking-item"
                                          key={staff.name}
                                        >
                                          <div className="ranking-number">
                                            {index + 1}
                                          </div>

                                          <div className="staff-ranking-avatar">
                                            {staff.name
                                              ?.charAt(0)
                                              ?.toUpperCase()}
                                          </div>

                                          <div className="staff-ranking-info">
                                            <div className="d-flex justify-content-between">
                                              <span className="fw-semibold">
                                                {staff.name}
                                              </span>

                                              <strong>
                                                {staff.totalActivity.toLocaleString()}
                                              </strong>
                                            </div>

                                            <div className="progress mt-2">
                                              <div
                                                className="progress-bar"
                                                style={{
                                                  width: `${
                                                    activeStaffData[0]
                                                      ?.totalActivity
                                                      ? Math.max(
                                                          2,
                                                          (staff.totalActivity /
                                                            activeStaffData[0]
                                                              .totalActivity) *
                                                            100,
                                                        )
                                                      : 0
                                                  }%`,
                                                }}
                                              ></div>
                                            </div>

                                            <small className="text-muted">
                                              {staff.staff_type}
                                            </small>
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Small UI improvements */}

      <style>
        {`
          .report-tabs {
            gap: 6px;
          }

          .report-tabs .nav-link {
            border-radius: 8px;
            font-weight: 500;
            transition: all .2s ease;
          }

          .report-tabs .nav-link:not(.active):hover {
            background: rgba(115, 103, 240, 0.08);
          }

          .avatar-initial {
            width: 38px;
            height: 38px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .table > :not(caption) > * > * {
            padding-top: 13px;
            padding-bottom: 13px;
          }

          .table thead th {
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: .35px;
          }

          .table tbody tr {
            transition: background .15s ease;
          }

          .table tbody tr:hover {
            background: rgba(115, 103, 240, 0.035);
          }

          .form-floating > label {
            font-size: 13px;
          }

          @media (max-width: 768px) {
            .report-tabs .nav-link {
              font-size: 13px;
            }
          }

          
    .analytics-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 20px;
      flex-wrap: wrap;
    }

    .analytics-summary {
      display: flex;
      gap: 10px;
    }

    .analytics-summary-item {
      min-width: 110px;
      padding: 10px 14px;
      border: 1px solid rgba(0,0,0,.08);
      border-radius: 10px;
      text-align: center;
    }

    .analytics-summary-label {
      display: block;
      font-size: 11px;
      color: #6f6b7d;
      margin-bottom: 2px;
    }

    .analytics-summary-item strong {
      font-size: 18px;
    }

    .analytics-summary-item small {
      margin-left: 2px;
      color: #6f6b7d;
    }

    .analytics-card {
      border: 1px solid rgba(0,0,0,.08);
      border-radius: 12px;
      padding: 18px;
      height: 100%;
    }

    .analytics-card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 15px;
    }

    .analytics-card-header h6 {
      font-weight: 600;
    }

    .analytics-card-badge {
      font-size: 11px;
      padding: 5px 9px;
      border-radius: 20px;
      background: rgba(115,103,240,.10);
      color: #7367f0;
      font-weight: 600;
    }

    .analytics-empty {
      min-height: 350px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
    }

    .analytics-empty-icon {
      width: 70px;
      height: 70px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(115,103,240,.10);
      color: #7367f0;
      font-size: 30px;
    }

    .activity-legend {
      display: flex;
      flex-direction: column;
      gap: 9px;
      margin-top: 5px;
    }

    .activity-legend-item {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
    }

    .activity-dot {
      width: 9px;
      height: 9px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .activity-name {
      color: #6f6b7d;
    }

    .staff-ranking {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .staff-ranking-item {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .ranking-number {
      width: 24px;
      font-size: 12px;
      font-weight: 600;
      color: #6f6b7d;
      text-align: center;
    }

    .staff-ranking-avatar {
      width: 38px;
      height: 38px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(115,103,240,.10);
      color: #7367f0;
      font-weight: 600;
      flex-shrink: 0;
    }

    .staff-ranking-info {
      flex: 1;
      min-width: 0;
    }

    .staff-ranking-info > div:first-child span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 75%;
    }

    .progress {
      height: 5px;
      border-radius: 10px;
      background: rgba(0,0,0,.06);
    }

    .progress-bar {
      border-radius: 10px;
      background: #7367f0;
    }

    .analytics-card .table th {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: .3px;
      color: #6f6b7d;
      white-space: nowrap;
    }

    .analytics-card .table td {
      font-size: 13px;
    }

    @media (max-width: 768px) {

      .analytics-header {
        align-items: flex-start;
      }

      .analytics-summary {
        width: 100%;
      }

      .analytics-summary-item {
        flex: 1;
      }

    }

        `}
      </style>
    </>
  );
};

export default StaffReport;
